import { BaseDataService } from './BaseDataService';
import { Organization } from '../../models';
import { NotificationService } from '../../services';
import { HasUploadImage } from '../../interfaces';
import { OpenBlobFile } from '../../utilities';

export class OrganizationDataService extends BaseDataService<Organization> implements HasUploadImage {
    constructor(notificationService: NotificationService) {
        super('Organization', 'Organizations', notificationService);
    }

    calculatePVA(organizationId: number) {
        const endpoint = `${this.endpointRoot}/calc/${organizationId}`;
        const init = this.buildRequestInit('POST');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }

            const message = `Error retrieving teams.`;
            this.notificationService.addError(message);
            throw new Error(message);
        });
    }

    calculateMidRanges(organizationId: number) {
        const endpoint = `${this.endpointRoot}/midranges/${organizationId}`;
        const init = this.buildRequestInit('POST');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }

            const message = `Error retrieving teams.`;
            this.notificationService.addError(message);
            throw new Error(message);
        });
    }

    getAllByUser() {
        const endpoint = `${this.endpointRoot}/byuser`;
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }

            const message = `Error retrieving teams.`;
            this.notificationService.addError(message);
            throw new Error(message);
        });
    }

    getTeamsForOrganization(organizationId: number) {
        const endpoint = `${this.endpointRoot}/${organizationId}/teams`;
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }

            const message = `Error retrieving teams.`;
            this.notificationService.addError(message);
            throw new Error(message);
        });
    }

    async getLogoImageData(organizationId: number): Promise<any> {
        const endpoint = `${this.endpointRoot}/${organizationId}/logo`;
        const init = this.buildRequestInit('GET');

        try {
            const response = await fetch(endpoint, init);
            let blob = await response.blob();
            let dataUrl = await new Promise(resolve => {
                let reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.readAsDataURL(blob);
            });
            dataUrl = dataUrl.toString();
            return dataUrl;
        } catch(e) {
            // No data
            return "";
        }
    }

    uploadImage(parentId: number, file: File) {
        const endpoint = `${this.endpointRoot}/${parentId}/uploadLogo`;
        const init = this.buildRequestInit('POST');

        const data = new FormData();
        data.append('imageFile', file);

        init.body = data;

        return fetch(endpoint, init).then(
            () => {
                this.notificationService.addNotification(`Logo uploaded successfully.`);
            },
            () => {
                this.notificationService.addError(`Error uploading logo. Please try again.`);
            }
        );
    }

    public deactivate(id: number): Promise<any> {
        const endpoint = `${this.endpointRoot}/${id}/deactivate`;
        const init = this.buildRequestInit('POST');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                this.notificationService.addNotification(`${this.entityType} deactivated successfully.`);
                return;
            }

            const message = `Error deactivating ${this.entityType.toLowerCase()}.`;
            this.notificationService.addError(message);
            throw new Error(message);
        });
    }

    public reactivate(id: number): Promise<any> {
        const endpoint = `${this.endpointRoot}/${id}/activate`;
        const init = this.buildRequestInit('POST');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                this.notificationService.addNotification(`${this.entityType} reactivated successfully.`);
                return;
            }

            const message = `Error reactivating ${this.entityType.toLowerCase()}.`;
            this.notificationService.addError(message);
            throw new Error(message);
        });
    }

    public getExpired(): Promise<any> {
        const endpoint = `${this.endpointRoot}/expired`;
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }

            const message = `Error retrieving expired organization data.`;
            this.notificationService.addError(message);
            throw new Error(message);
        });
    }

    public getExpiringCSV() {
        const endpoint = `${this.endpointRoot}/expiring/csv`;
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            // this returns a SurveyAssessmentViewModel
            if (response.ok) {
                return response.blob();
            }
            const message = `Error retrieving survey responses.`;
            this.notificationService.addError(message);
            throw new Error(message);
        }).then((blobData: any) => {
            var d = new Date();
            return OpenBlobFile(blobData, 'Expiring-Orgs-' + (d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate()) + '.csv');
        });
    }

    public getExpiredCSV() {
        const endpoint = `${this.endpointRoot}/expired/csv`;
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            // this returns a SurveyAssessmentViewModel
            if (response.ok) {
                return response.blob();
            }
            const message = `Error retrieving survey responses.`;
            this.notificationService.addError(message);
            throw new Error(message);
        }).then((blobData: any) => {
            var d = new Date();
            return OpenBlobFile(blobData, 'Expired-Orgs-' + (d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate()) + '.csv');
        });
    }
    
    calculateTeamReport(orgId: number) {
        const endpoint = `${this.endpointRoot}/calculateTeamReport/${orgId}`;
        const init = this.buildRequestInit('POST');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }

            const message = `Error retrieving teams.`;
            this.notificationService.addError(message);
            throw new Error(message);
        });
    }

    calculateTeamReportCandidates(orgId: number) {
        const endpoint = `${this.endpointRoot}/calculateTeamReport/candidates/${orgId}`;
        const init = this.buildRequestInit('POST');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }

            const message = `Error retrieving teams.`;
            this.notificationService.addError(message);
            throw new Error(message);
        });
    }

    calculateTeamReportFormer(orgId: number) {
        const endpoint = `${this.endpointRoot}/calculateTeamReport/former/${orgId}`;
        const init = this.buildRequestInit('POST');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.json();
            }

            const message = `Error retrieving teams.`;
            this.notificationService.addError(message);
            throw new Error(message);
        });
    }

    getAllUsersReport(orgId: number): Promise<any> {
        const endpoint = `${this.endpointRoot}/${orgId}/report/allUsers`;
        const init = this.buildRequestInit('GET');

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                return response.blob();
            }
            const message = `Error retrieving all-users report.`;
            this.notificationService.addError(message);
            throw new Error(message);
        }).then((blobData: any) => {
            return OpenBlobFile(blobData, `allUsersForOrganization-${orgId}.xlsx`);
        });
    }

    public testEmail(orgId: number, surveyId: number): Promise<any> {
        const endpoint = `${this.endpointRoot}/${orgId}/${surveyId}/testEmail`;
        const init = this.buildRequestInit('POST', { });

        return fetch(endpoint, init).then((response) => {
            if (response.ok) {
                this.notificationService.addNotification('Email Sent');
                return;
            }

            this.notificationService.addError('Error saving question sort order.');
            return;
        });
    }
}


// WEBPACK FOOTER //
// ./src/services/api/OrganizationData.tsx